import React from "react";

export default function SearchableUserSelect({
  value,
  onSelect,
  options = [],
  loading,
  placeholder = "Search users…",
  onQueryChange,
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [highlight, setHighlight] = React.useState(0);
  const boxRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const suppressNextQuery = React.useRef(false);

  React.useEffect(() => {
    const onDocPointerDown = (e) => {
      const path = typeof e.composedPath === "function" ? e.composedPath() : [];
      const inside =
        boxRef.current &&
        (path.includes(boxRef.current) || boxRef.current.contains(e.target));
      if (!inside) setOpen(false);
    };
    document.addEventListener("pointerdown", onDocPointerDown);
    return () => document.removeEventListener("pointerdown", onDocPointerDown);
  }, []);

  React.useEffect(() => {
    const t = setTimeout(() => {
      if (suppressNextQuery.current) {
        suppressNextQuery.current = false; // consume the suppression
        return;
      }
      onQueryChange?.(query);
    }, 300);
    return () => clearTimeout(t);
  }, [query, onQueryChange]);

  React.useEffect(() => {
    if (document.activeElement === inputRef.current) setOpen(true);
  }, [options, loading]);

  React.useEffect(() => {
    if (options.length === 0) setHighlight(0);
    else setHighlight((i) => Math.max(0, Math.min(i, options.length - 1)));
  }, [options.length]);

  const handleKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      return;
    }
    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const picked = options[highlight];
      if (picked) {
        suppressNextQuery.current = true;        // <<< prevent fetch
        onSelect(picked.username);
        setQuery(picked.username);               // updates input without refetch
        setOpen(false);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const displayValue = React.useMemo(() => (query ? query : value || ""), [query, value]);

  return (
    <div className="cbx" ref={boxRef}>
      <input
        ref={inputRef}
        className="cbx-input"
        type="text"
        placeholder={placeholder}
        value={displayValue}
        onChange={(e) => {
          if (value) onSelect("");
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        autoComplete="off"
      />

      {(value || query) && (
        <button
          type="button"
          className="cbx-clear"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            onSelect("");
            setQuery("");
            setOpen(true);
          }}
          aria-label="Clear"
        >
          ×
        </button>
      )}

      <span className="cbx-chevron" aria-hidden>▾</span>

      {open && (
        <ul
          className="cbx-menu"
          role="listbox"
          aria-label="Users"
          onMouseDown={(e) => e.preventDefault()}
        >
          {!loading && query.length > 0 && query.length < 3 && (
            <li className="cbx-item muted">Type at least 3 characters…</li>
          )}
          {loading && <li className="cbx-item muted">Loading…</li>}
          {!loading && options.length === 0 && (
            <li className="cbx-item muted">Type 3+ characters…</li>
          )}
          {!loading &&
            options.map((u, idx) => (
              <li
                key={u.id ?? u.username}
                role="option"
                aria-selected={value === u.username}
                className={`cbx-item ${idx === highlight ? "is-active" : ""}`}
                onMouseEnter={() => setHighlight(idx)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  suppressNextQuery.current = true;  // <<< prevent fetch
                  onSelect(u.username);
                  setQuery(u.username);
                  setOpen(false);
                }}
                onClick={() => {
                  suppressNextQuery.current = true;
                  onSelect(u.username);
                  setQuery(u.username);
                  setOpen(false);
                }}
              >
                {u.username ?? u.userName ?? u.displayName ?? u.email ?? u.visorId ?? "Unknown"}
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
